import React, { useState } from 'react';
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Wrench, Zap, Home, Hammer, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const iconMap = {
  Sparkles,
  Wrench,
  Zap,
  ChefHat: Home,
  Home,
  Hammer
};

const EnhancedServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = iconMap[service.icon];
  const isSvgIcon =
    typeof service.icon === 'string' && service.icon.toLowerCase().endsWith('.svg');

  // 3D tilt effect
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  const handleMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set(event.clientX - centerX);
    y.set(event.clientY - centerY);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    setIsHovered(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{
        perspective: 1000,
        height: "100%", // ensure parent fills available height
        display: "flex",
        flexDirection: "column"
      }}
      className="h-full flex flex-col"
    >
      <motion.div
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={handleMouseLeave}
        style={{
          rotateX,
          rotateY,
          transformStyle: "preserve-3d",
          height: "100%",
          display: "flex",
          flexDirection: "column"
        }}
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        className="flex flex-col h-full"
      >
        {/* 
          Card ke height barabar krne ke liye:
          - grid container (parent) pe items-stretch aur h-full lagao. (Home.jsx)
        */}
        <Card className="group relative overflow-hidden bg-white border-2 border-gray-100 rounded-3xl p-8 hover:border-teal-500 transition-all duration-300 cursor-pointer flex flex-col h-full min-h-[370px]">
          {/* Gradient overlay on hover */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 0.05 : 0 }}
            className="absolute inset-0 bg-gradient-to-br from-teal-500 to-green-500 pointer-events-none"
          />

          <div className="flex-1 flex flex-col">
            {/* Icon with animation */}
            <motion.div
              animate={{
                scale: isHovered ? 1.1 : 1,
                rotate: isHovered ? 360 : 0
              }}
              transition={{ duration: 0.5 }}
              className="mb-6 w-16 h-16 bg-gradient-to-br from-teal-100 to-green-100 rounded-2xl flex items-center justify-center relative shrink-0"
              style={{ transform: "translateZ(50px)" }}
            >
              {Icon ? (
                <Icon className="w-8 h-8 text-teal-600" />
              ) : (
                <img
                  src={service.icon}
                  alt={`${service.title} icon`}
                  className="w-8 h-8 object-contain"
                />
              )}
              {isHovered && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 border-2 border-teal-400 rounded-2xl"
                />
              )}
            </motion.div>

            {/* Content */}
            <h3 className="text-2xl font-bold text-gray-900 mb-1" style={{ transform: "translateZ(30px)" }}>
              {service.title}
            </h3>
            <p className="text-gray-600 mb-4 leading-relaxed" style={{ transform: "translateZ(20px)" }}>
              {service.description}
            </p>

            {/* Features with checkmarks */}
            <div className="mb-4 space-y-2" style={{ transform: "translateZ(20px)" }}>
              {service.features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 + idx * 0.1 }}
                  className="flex items-center text-sm text-gray-600"
                >
                  <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                  {feature}
                </motion.div>
              ))}
            </div>
            {/* Push price/CTA to bottom */}
            <div className="flex-1"></div> 
          </div>

          {/* Price and CTA */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-100 mt-2" style={{ transform: "translateZ(30px)" }}>
            <div>
              <span className="text-sm text-gray-500 block">Starting at</span>
              <motion.p
                animate={{ scale: isHovered ? 1.1 : 1 }}
                className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent"
              >
                {service.startingPrice}
              </motion.p>
            </div>
            <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.95 }}>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 shadow-lg"
              >
                Book Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </motion.div>
          </div>

          {/* Shine effect on hover */}
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: isHovered ? '200%' : '-100%' }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
          />
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default EnhancedServiceCard;
